/**
 * Intellectual learning math — Subject ranks, learning-session/resource XP, and
 * level helpers. (Historically this file also held the academic-field "spheres"
 * config; those were retired in IN4 — the 7 Knowledge Paths live in
 * `intellectualPaths.ts`, and a Path's level derives from its Subjects.)
 *
 * The `IntellectualSphereName` union is kept ONLY for the legacy→Path resolver
 * (`LEGACY_SPHERE_TO_PATH` in `intellectualPaths.ts`); no live data is typed by it.
 */
export type IntellectualSphereName =
  | 'Wellbeing'
  | 'Society'
  | 'Commerce'
  | 'Ecology'
  | 'Innovation'
  | 'Creativity'
  | 'Justice';

export type SubjectRank =
  | 'Uninitiated'
  | 'Novice'
  | 'Apprentice'
  | 'Student'
  | 'Scholar'
  | 'Practitioner'
  | 'Adept'
  | 'Specialist'
  | 'Expert'
  | 'Sage'
  | 'Luminary';

export const SUBJECT_RANKS: { rank: SubjectRank; minLevel: number; description: string }[] = [
  { rank: 'Uninitiated', minLevel: 0, description: 'No knowledge yet - starting point' },
  { rank: 'Novice', minLevel: 1, description: 'Just beginning to explore' },
  { rank: 'Apprentice', minLevel: 10, description: 'Learning the fundamentals' },
  { rank: 'Student', minLevel: 20, description: 'Actively studying and building knowledge' },
  { rank: 'Scholar', minLevel: 30, description: 'Developing solid understanding' },
  { rank: 'Practitioner', minLevel: 40, description: 'Applying knowledge practically' },
  { rank: 'Adept', minLevel: 50, description: 'Skilled comprehension and application' },
  { rank: 'Specialist', minLevel: 60, description: 'Focused expertise in the subject' },
  { rank: 'Expert', minLevel: 70, description: 'Deep mastery and insight' },
  { rank: 'Sage', minLevel: 80, description: 'Wisdom and advanced insight' },
  { rank: 'Luminary', minLevel: 90, description: 'Pinnacle of understanding' }
];

export const SUBJECT_RANK_COLORS: Record<SubjectRank, string> = {
  'Uninitiated': '#6B7280',
  'Novice': '#9CA3AF',
  'Apprentice': '#10B981',
  'Student': '#14B8A6',
  'Scholar': '#3B82F6',
  'Practitioner': '#6366F1',
  'Adept': '#8B5CF6',
  'Specialist': '#D946EF',
  'Expert': '#F59E0B',
  'Sage': '#EF4444',
  'Luminary': '#FBBF24'
};

export const SESSION_TYPES = [
  { value: 'reading', label: 'Reading', icon: 'BookOpen', xpMultiplier: 1.0 },
  { value: 'course', label: 'Course Work', icon: 'GraduationCap', xpMultiplier: 1.2 },
  { value: 'lecture', label: 'Lecture', icon: 'Presentation', xpMultiplier: 1.1 },
  { value: 'tutorial', label: 'Tutorial', icon: 'PlayCircle', xpMultiplier: 1.0 },
  { value: 'practice', label: 'Practice', icon: 'Dumbbell', xpMultiplier: 1.3 },
  { value: 'research', label: 'Research', icon: 'Search', xpMultiplier: 1.4 },
  { value: 'review', label: 'Review', icon: 'RefreshCw', xpMultiplier: 0.8 }
] as const;

export const RESOURCE_TYPES = [
  { value: 'book', label: 'Book', icon: 'Book', baseXP: 100 },
  { value: 'course', label: 'Course', icon: 'GraduationCap', baseXP: 200 },
  { value: 'article', label: 'Article', icon: 'FileText', baseXP: 25 },
  { value: 'video', label: 'Video', icon: 'Video', baseXP: 50 },
  { value: 'podcast', label: 'Podcast', icon: 'Headphones', baseXP: 40 },
  { value: 'note', label: 'Note', icon: 'StickyNote', baseXP: 15 }
] as const;

export const BASE_XP_PER_MINUTE = 2;
export const COMPLEXITY_MULTIPLIERS: Record<'low' | 'medium' | 'high', number> = {
  low: 0.8,
  medium: 1.0,
  high: 1.5
};

// Numeric lookups for the XP math — mutable HYDRATION TARGETS (console →
// XP economy, migration 0193). The `as const` arrays above stay the source
// of labels/icons/type unions; these records hold the tunable numbers,
// seeded with the same defaults.
export const SESSION_TYPE_MULTIPLIERS: Record<string, number> = Object.fromEntries(
  SESSION_TYPES.map((s) => [s.value, s.xpMultiplier]),
);
export const RESOURCE_TYPE_XP: Record<string, number> = Object.fromEntries(
  RESOURCE_TYPES.map((r) => [r.value, r.baseXP]),
);
// Learning XP per minute (mutable; BASE_XP_PER_MINUTE is the default).
export const LEARNING_XP = { perMinute: BASE_XP_PER_MINUTE };

export function hydrateIntellectualXp(
  actions: Array<{ key: string; base_xp: number; category: string }>,
  rates: Array<{ kind: string; key: string; multiplier: number }>,
): void {
  for (const a of actions) {
    if (a.category !== 'intellectual' || !Number.isFinite(a.base_xp)) continue;
    if (a.key === 'learn_minute' && a.base_xp > 0) LEARNING_XP.perMinute = a.base_xp;
    const resourceKey = a.key.startsWith('learn_resource_') ? a.key.slice('learn_resource_'.length) : null;
    if (resourceKey && resourceKey in RESOURCE_TYPE_XP && a.base_xp >= 0) {
      RESOURCE_TYPE_XP[resourceKey] = a.base_xp;
    }
  }
  for (const r of rates) {
    if (!Number.isFinite(r.multiplier) || r.multiplier <= 0) continue;
    if (r.kind === 'learning_complexity' && r.key in COMPLEXITY_MULTIPLIERS) {
      COMPLEXITY_MULTIPLIERS[r.key as 'low' | 'medium' | 'high'] = r.multiplier;
    }
    if (r.kind === 'learning_session_type' && r.key in SESSION_TYPE_MULTIPLIERS) {
      SESSION_TYPE_MULTIPLIERS[r.key] = r.multiplier;
    }
  }
}

export function getSubjectRankFromLevel(level: number): SubjectRank {
  for (let i = SUBJECT_RANKS.length - 1; i >= 0; i--) {
    if (level >= SUBJECT_RANKS[i].minLevel) {
      return SUBJECT_RANKS[i].rank;
    }
  }
  return 'Uninitiated';
}

export function getSubjectLevelFromXP(xp: number): number {
  let currentLevel = 0;
  let xpThreshold = 0;
  const baseXPPerLevel = 100;

  while (currentLevel < 100) {
    xpThreshold += baseXPPerLevel + (currentLevel * 10);
    if (xp < xpThreshold) {
      return currentLevel;
    }
    currentLevel++;
  }
  return 100;
}

export function getXPForLevel(level: number): number {
  let totalXP = 0;
  const baseXPPerLevel = 100;

  for (let i = 0; i < level; i++) {
    totalXP += baseXPPerLevel + (i * 10);
  }
  return totalXP;
}

export function getSubjectRankProgress(level: number): {
  currentRank: SubjectRank;
  nextRank: SubjectRank | null;
  progress: number;
  levelsToNext: number;
} {
  let currentIndex = 0;
  for (let i = SUBJECT_RANKS.length - 1; i >= 0; i--) {
    if (level >= SUBJECT_RANKS[i].minLevel) {
      currentIndex = i;
      break;
    }
  }

  const current = SUBJECT_RANKS[currentIndex];
  const next = currentIndex < SUBJECT_RANKS.length - 1 ? SUBJECT_RANKS[currentIndex + 1] : null;

  if (!next) {
    return { currentRank: current.rank, nextRank: null, progress: 100, levelsToNext: 0 };
  }

  const progressLevels = level - current.minLevel;
  const rangeLevels = next.minLevel - current.minLevel;
  const progress = Math.round((progressLevels / rangeLevels) * 100);
  const levelsToNext = next.minLevel - level;

  return { currentRank: current.rank, nextRank: next.rank, progress, levelsToNext };
}

export function calculateSessionXP(durationMinutes: number, sessionType: string, complexity: 'low' | 'medium' | 'high'): number {
  // Config-backed (console → XP economy); defaults = the constants above.
  const typeMultiplier = SESSION_TYPE_MULTIPLIERS[sessionType] || 1.0;
  const complexityMultiplier = COMPLEXITY_MULTIPLIERS[complexity];

  return Math.round(durationMinutes * LEARNING_XP.perMinute * typeMultiplier * complexityMultiplier);
}

export function calculateResourceXP(resourceType: string): number {
  return RESOURCE_TYPE_XP[resourceType] || 25;
}
